import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle } from "lucide-react";

export default function CancelEventDialog({ event, open, onOpenChange, onDone }) {
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState("");
  const isCancel = confirm.trim().toUpperCase() === "CANCEL";

  const submit = async () => {
    if (!isCancel) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/events/${event.id}`);
      if (data.mode === "deleted") {
        toast.success("Event deleted.");
      } else {
        const refunded = (data.refunds || []).filter((r) => r.ok).length;
        const failed = (data.refund_errors || []).length;
        toast.success(
          `Event cancelled. ${data.cancelled_applications} applicant(s) notified · ${refunded} refund(s) issued` +
          (failed ? ` · ${failed} refund(s) need review` : "")
        );
      }
      onDone?.(data);
      onOpenChange(false);
      setConfirm("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="cancel-event-dialog" className="max-w-lg">
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div
              className="rounded-full p-2"
              style={{ background: "rgba(31,93,52,0.15)", color: "var(--terracotta)" }}
            >
              <AlertTriangle className="h-5 w-5" />
            </div>
            <AlertDialogTitle className="font-serif text-2xl">
              Cancel “{event?.title}”?
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription asChild>
            <div className="mt-4 space-y-3 text-sm" style={{ color: "var(--ink-soft)" }}>
              <p>This can’t be undone. Once you cancel:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>The event is removed from public listings.</li>
                <li>
                  Every applicant is notified in-app that the event was cancelled.
                </li>
                <li>
                  Every paid vendor is refunded <strong>100 %</strong> of their booth fee.
                  The refund is pulled back from your Connect payout — AddVendTour retains
                  its 5 % platform fee.
                </li>
                <li>All open applications are marked cancelled.</li>
              </ul>
              <div
                className="rounded-sm p-3 text-xs border mt-4"
                style={{
                  backgroundColor: "rgba(31,93,52,0.06)",
                  borderColor: "rgba(31,93,52,0.25)",
                }}
                data-testid="cancel-confirm-hint"
              >
                Type <strong>CANCEL</strong> to confirm.
              </div>
              <input
                type="text"
                autoFocus
                data-testid="cancel-confirm-input"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="CANCEL"
                className="mt-1 w-full rounded-sm border px-3 py-2 text-sm outline-none focus:border-[color:var(--terracotta)]"
                style={{ borderColor: "var(--border-warm)" }}
              />
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            data-testid="cancel-abort"
            disabled={busy}
            className="rounded-full"
            onClick={() => setConfirm("")}
          >
            Keep event
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="cancel-confirm"
            disabled={!isCancel || busy}
            onClick={submit}
            className="rounded-full"
            style={{
              background: isCancel ? "var(--terracotta)" : undefined,
              color: isCancel ? "#fff" : undefined,
              opacity: isCancel ? 1 : 0.6,
            }}
          >
            {busy ? "Cancelling…" : "Cancel event & refund"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
